<template>
  <aside class="order-list-pane" @scroll.passive="handleScroll">
    <section class="order-list-card">
      <div class="order-list-heading">
        <h2>生产订单</h2>
        <span>{{ orders.length }} / {{ total }} 条</span>
      </div>
      <form class="order-search" @submit.prevent="searchOrders">
        <input v-model="keyword" type="search" placeholder="订单号 / 名称 / 客户 / 型号">
        <button type="submit" :disabled="loading">查询</button>
        <button type="button" class="reset-button" :disabled="loading" @click="resetSearch">重置</button>
      </form>
      <button v-for="order in orders" :key="order.id" type="button" class="order-item"
        :class="{ active: isSelected(order) }" @click="$emit('select', order)">
        <span class="order-item-top">
          <strong>{{ orderCode(order) || '未填写订单号' }}</strong>
          <span class="order-priority">{{ priorityName(order) }}</span>
        </span>
        <span class="order-name">{{ order.name || order.productionOrderName || '未填写订单名称' }}</span>
        <span class="order-meta">客户：{{ displayValue(order.customer) }}</span>
        <span class="order-meta">型号：{{ orderModel(order) }} · 数量：{{ orderQuantity(order) }}</span>
        <span class="order-open">查看订单详情 →</span>
      </button>
      <div v-if="!loading && !orders.length" class="empty-orders">暂无订单数据</div>
      <div class="order-list-state">
        <ASpin v-if="loading" size="small" />
        <span v-else-if="finished">没有更多数据</span>
        <span v-else>下滑加载更多</span>
      </div>
    </section>
  </aside>
</template>

<script>
import { dataAuthOrders } from '@api'

export default {
  name: 'OrderList',
  data() {
    return {
      keyword: '',
      submittedKeyword: '',
      orders: [],
      pageNo: 1,
      pageSize: 10,
      total: 0,
      loading: false,
      finished: false,
      requestId: 0,
    }
  },
  props: {
    selectedOrderId: {
      type: [String, Number],
      default: null,
    },
  },
  mounted() {
    this.loadOrders(true)
  },
  methods: {
    async loadOrders(reset = false) {
      if (reset) {
        this.requestId += 1
        this.orders = []
        this.pageNo = 1
        this.total = 0
        this.finished = false
      } else if (this.loading || this.finished) {
        return
      }

      const requestId = ++this.requestId
      const requestPage = this.pageNo
      const params = { pageNo: requestPage, pageSize: this.pageSize }
      if (this.submittedKeyword.trim()) params.keyword = this.submittedKeyword.trim()
      this.loading = true

      try {
        const response = await dataAuthOrders({ params })
        const payload = response && response.data !== undefined ? response.data : response
        const pageData = payload && payload.data && !Array.isArray(payload.data) ? payload.data : payload
        const records = Array.isArray(pageData && pageData.data)
          ? pageData.data
          : Array.isArray(pageData && pageData.records)
            ? pageData.records
            : []

        if (requestId !== this.requestId) return
        this.orders = this.orders.concat(records)
        this.total = Number(pageData.total || pageData.totalCount) || this.orders.length
        this.pageNo += 1
        this.finished = !records.length || this.orders.length >= this.total
      } catch (error) {
        if (requestId === this.requestId) {
          this.$message.error('订单列表加载失败，请稍后重试。')
        }
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    searchOrders() {
      this.submittedKeyword = this.keyword.trim()
      this.loadOrders(true)
    },
    resetSearch() {
      this.keyword = ''
      this.submittedKeyword = ''
      this.loadOrders(true)
    },
    handleScroll(event) {
      const { scrollTop, clientHeight, scrollHeight } = event.target
      if (scrollTop + clientHeight >= scrollHeight - 24) this.loadOrders()
    },
    isSelected(order) {
      return String(this.selectedOrderId || '') === String(order.id)
    },
    orderCode(order) {
      return order.code || order.productionOrderCode || order.orderCode || ''
    },
    orderModel(order) {
      const materials = order.productionOrder2Materiels || order.materials || []
      return order.model || (materials[0] && materials[0].model) || '未填写'
    },
    orderQuantity(order) {
      const materials = order.productionOrder2Materiels || order.materials || []
      const material = materials[0] || {}
      return order.quantity || order.orderQuantity || material.orderNum || order.materielNums || '未填写'
    },
    priorityName(order) {
      const priority = order.priority
      return typeof priority === 'object' ? priority.name : (priority || '普通优先级')
    },
    displayValue(value) {
      if (value === null || value === undefined || value === '') return '未填写'
      if (typeof value === 'object') return value.name || value.code || '未填写'
      return value
    },
  },
}
</script>

<style lang="less" scoped>
.order-list-pane {
  box-sizing: border-box;
  width: 420px;
  min-width: 420px;
  height: 100%;
  min-height: 0;
  padding: 14px;
  overflow-y: auto;
  font-size: 16px;
  background: #edf2f7;
  border-right: 1px solid #d5e0ea;
}

.order-list-card {
  box-sizing: border-box;
  min-height: 100%;
  padding: 16px;
  background: #fff;
  border: 1px solid #d9e4ee;
  border-radius: 16px;
}

.order-list-heading,
.order-item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.order-list-heading {
  padding-bottom: 10px;
  border-bottom: 1px solid #dce5ee;
}

.order-list-heading h2 {
  margin: 0;
  color: #123f63;
  font-size: 22px;
}

.order-list-heading > span,
.order-meta,
.order-list-state {
  color: #67839a;
  font-size: 14px;
}

.order-search {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 54px 54px;
  gap: 6px;
  margin: 12px 0;
}

.order-search input {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: 36px;
  padding: 0 10px;
  font-size: 15px;
  color: #173d5b;
  background: #f3f7fa;
  border: 1px solid #d3e0e9;
  border-radius: 8px;
  outline: none;
}

.order-search button {
  height: 36px;
  font-size: 15px;
  color: #fff;
  cursor: pointer;
  background: #1678dc;
  border: 1px solid #1678dc;
  border-radius: 8px;
}

.order-search button:disabled {
  cursor: not-allowed;
  opacity: .6;
}

.order-search .reset-button {
  color: #315672;
  background: #fff;
  border-color: #cbd9e5;
}

.order-item {
  display: grid;
  gap: 7px;
  width: 100%;
  padding: 13px;
  margin-top: 9px;
  color: #173d5b;
  text-align: left;
  cursor: pointer;
  background: #fff;
  border: 1px solid #d6e2ed;
  border-radius: 12px;
}

.order-item:hover,
.order-item.active {
  background: #f2f8ff;
  border-color: #1678dc;
}

.order-item-top strong {
  color: #123f63;
  font-size: 17px;
}

.order-priority {
  padding: 3px 7px;
  color: #176ed0;
  font-size: 13px;
  background: #eaf4ff;
  border-radius: 6px;
}

.order-name {
  color: #243c52;
  font-size: 16px;
  line-height: 1.5;
}

.order-open {
  margin-top: 2px;
  color: #1678dc;
  font-size: 14px;
}

.empty-orders {
  padding: 26px 8px;
  color: #7890a4;
  font-size: 16px;
  text-align: center;
}

.order-list-state {
  display: flex;
  justify-content: center;
  min-height: 28px;
  padding-top: 10px;
}

@media (max-width: 900px) {
  .order-list-pane {
    width: 100%;
    min-width: 0;
    max-height: 34vh;
    padding: 10px 12px;
    border-right: 0;
    border-bottom: 1px solid #d5e0ea;
  }
}
</style>
